// Essay Marking — the learner writes a CAPS History essay and the marking
// engine reads it, inside a course.
//
// THE STUDENT VIEW IS AN EMBED of the real Essay Lab (the marking-coach app,
// deployed at essaylab.kulalearn.com), not a re-implementation. We tried the
// re-implementation: the report alone was six ported files that drifted the
// moment the original changed, and the write/marking stages were never ported
// at all, so learners saw a half-copy. The lab IS the designed experience —
// embedding it means it always looks like itself, and updates when its own
// repo deploys. What the LMS owns is everything the iframe cannot be trusted
// with: auth, the marking allowance, credit metering, and recording results.
//
// Money still flows through OUR door. The handshake below hands the lab the
// learner's access token, and the lab calls this platform's metered endpoints
// (/api/ai/mark-essay, /api/ai/transcribe-essay) with it — the allowance hold
// and the credit reservation happen server-side exactly as before. The lab's
// deployment has NO Anthropic key, so there is no unmetered path.
//
// ONE LAB, TWO BOARDS. The slide's `board` ('nsc' | 'ieb') rides the handshake
// and every marking request. It picks the question set, the memo and the
// marking grid — the lab, the pipeline, the report, the allowance and the
// metering are identical either way, which is why this is a setting and not a
// second slide type. The endpoint is the authority: it re-resolves the pack for
// {board, topicId, year, sitting, paper} and refuses if there isn't one.
//
// Handshake (the standard webapp-embed protocol, plus a config payload):
//   lab   → slide   {event:'ready'}            (repeated for 20s or until
//                                               answered — and re-sent whenever
//                                               the lab wants a fresh token,
//                                               e.g. on a 401)
//   slide → lab     lms-init{config:{token, apiOrigin, courseId, board, question}}
//   lab   → slide   {event:'started'}          marking began
//   lab   → slide   {event:'completed', data}  essay + full report — recorded
//                                              via onActivityResponse so
//                                              teachers can read both later.

import { useCallback, useEffect, useRef, useState } from 'react';
import type { EssayMarkingSlideData, Theme } from '../slideTypes';
import { slideTypographyStyle } from '../slideTypes';
import { supabase } from '../../../../lib/supabase';
import { reportError } from '../../../../lib/errorHandling';
import type { ActivityResponsePayload } from '../../../../types';
import { isEmbedMessage, type EmbedEventPayload } from '../../../../types';
import { BOARDS, BOARD_LABELS, asBoard, type Board } from '../../../../lib/marking/board';

interface Props {
  data: EssayMarkingSlideData;
  theme: Theme;
  isEditing: boolean;
  /**
   * The course this slide sits in. Handed to the lab so it can send it back
   * with the marking request — without it the endpoint cannot tell which
   * course's allowance to draw from, and a per-course pool silently becomes a
   * global one.
   */
  courseId?: string;
  onUpdate: (data: EssayMarkingSlideData) => void;
  onActivityResponse?: (response: ActivityResponsePayload) => void;
}

// Where the lab lives — now shared with the saved-report viewer in My Learning
// Journey, because the value is both the iframe src and the origin filter and a
// drifted copy would silently drop every message.
import { LAB_ORIGIN } from '../../../../lib/essayLab';

/**
 * The result of trying to read the session. `ok: false` means the auth layer
 * would not answer (see readToken) — deliberately NOT the same as a null token,
 * which means the learner really is signed out.
 */
type TokenRead = { ok: true; token: string | null } | { ok: false };

/**
 * The lab's `completed` record, as the activity response the LMS stores.
 * `activity_type` and `mode` are free text on activity_responses (no CHECK);
 * 'essay-marking' follows the hyphenated kinds the table's comment lists, and
 * 'survey' because a marking has no right/wrong. The whole record — board,
 * question coordinates, band, essay, report — becomes response_data, which is
 * what a teacher-side reader would want to see.
 */
export function toActivityResponse(data: unknown): ActivityResponsePayload {
  const record = (data && typeof data === 'object' ? data : { value: data }) as Record<string, unknown>;
  return { activityType: 'essay-marking', mode: 'survey', responseData: record };
}

export function EssayMarkingSlide({ data, theme, isEditing, courseId, onUpdate, onActivityResponse }: Props) {
  if (isEditing) return <EssayMarkingEditor data={data} theme={theme} onUpdate={onUpdate} />;
  return <EssayMarkingStudent data={data} courseId={courseId} onActivityResponse={onActivityResponse} />;
}

function EssayMarkingStudent({
  data,
  courseId,
  onActivityResponse,
}: {
  data: EssayMarkingSlideData;
  courseId?: string;
  onActivityResponse?: (response: ActivityResponsePayload) => void;
}) {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [blocked, setBlocked] = useState<string | null>(null);

  // The token we hand the lab, kept current by onAuthStateChange below.
  //
  // IT IS HELD HERE BECAUSE ANSWERING `ready` MUST NOT AWAIT ANYTHING. The
  // handshake used to call supabase.auth.getSession() per `ready`, which reads
  // fine and strands learners: getSession() waits on the auth lock shared with
  // every other tab on this origin (the course builder, a second learner tab),
  // and after 10s it REJECTS rather than resolving. The rejection went into a
  // `void sendInit()` with no catch, so nothing was posted, nothing was logged,
  // and the lab — which gives up announcing `ready` after 20s — fell back to
  // its question picker with "Couldn't load the question list", pointing at an
  // endpoint that was never the problem. That is the intermittent bug: it needs
  // a second tab or a slow refresh, so it comes and goes.
  const tokenRef = useRef<string | null>(null);
  // Whether an init has gone down the wire yet. A `ready` after that is the lab
  // asking for a FRESHER token (it re-runs the handshake on a 401), so we
  // answer from cache immediately and re-answer if the session has moved on.
  const postedRef = useRef(false);

  const post = useCallback(
    (token: string) => {
      const frame = iframeRef.current?.contentWindow;
      if (!frame) return;
      postedRef.current = true;
      frame.postMessage(
        {
          type: 'lms-init',
          version: '1.0',
          payload: {
            config: {
              token,
              apiOrigin: window.location.origin,
              courseId: courseId ?? null,
              // Always a concrete board, never the raw field: a slide authored
              // before boards existed has none, and the lab would then have to
              // guess what a missing value means. asBoard settles it here, in
              // the one place that knows the slide.
              board: asBoard(data.board),
              question: data.topicId
                ? { topicId: data.topicId, year: data.year, sitting: data.sitting, paper: data.paper, prompt: data.questionLabel }
                : null,
            },
          },
        },
        LAB_ORIGIN,
      );
    },
    [data.board, data.topicId, data.year, data.sitting, data.paper, data.questionLabel, courseId],
  );

  /** One in-flight getSession at a time — the lab re-announces every 1.5s. */
  const readingRef = useRef<Promise<TokenRead> | null>(null);

  /**
   * Read the session, tolerating the lock timeout. Never rejects, and reports
   * "couldn't read" separately from "there is no session": a contended lock is
   * not a signed-out learner, and telling them to sign in would be a lie.
   */
  const readToken = useCallback((): Promise<TokenRead> => {
    return (readingRef.current ??= (async () => {
      try {
        const { data: session } = await supabase.auth.getSession();
        const token = session.session?.access_token ?? null;
        if (token) tokenRef.current = token;
        return { ok: true, token } as TokenRead;
      } catch (e) {
        // Reported rather than swallowed: this failure used to leave nothing
        // behind at all, which is why it took a screenshot to find.
        reportError('essayMarking.getSession', e);
        return { ok: false } as TokenRead;
      } finally {
        readingRef.current = null;
      }
    })());
  }, []);

  const sendInit = useCallback(async () => {
    // Fast path: answer now, from the token onAuthStateChange keeps current.
    const known = tokenRef.current;
    const repeat = postedRef.current;
    if (known) post(known);

    // A repeat `ready` means the lab wants a newer token, and on first contact
    // we may have none yet — either way, go and look. This runs after the fast
    // path, so a contended lock delays only the re-answer, never the answer.
    if (!known || repeat) {
      const read = await readToken();
      if (read.ok && read.token && read.token !== known) post(read.token);
      else if (read.ok && !read.token && !known) {
        setBlocked('You need to be signed in to have an essay marked.');
      }
    }
  }, [post, readToken]);

  // Supabase pushes the session on INITIAL_SESSION, SIGNED_IN and
  // TOKEN_REFRESHED, so tokenRef is current without anyone asking for it — this
  // is what lets `ready` be answered synchronously.
  useEffect(() => {
    const { data: sub } = supabase.auth.onAuthStateChange((_event, session) => {
      const token = session?.access_token ?? null;
      const changed = token !== null && token !== tokenRef.current;
      tokenRef.current = token;
      // A refreshed token is worth pushing down: the lab may be sitting on an
      // expired one mid-essay, and it only re-handshakes after a failed call.
      if (changed && postedRef.current) post(token);
      if (token) setBlocked(null);
    });
    return () => sub.subscription.unsubscribe();
  }, [post]);

  useEffect(() => {
    const onMessage = (e: MessageEvent) => {
      if (e.origin !== LAB_ORIGIN) return;
      if (!isEmbedMessage(e.data)) return;
      const payload = e.data.payload as EmbedEventPayload;
      if (payload.event === 'ready') {
        void sendInit();
      } else if (payload.event === 'completed' && payload.data) {
        // The learner's essay and the full report, saved as their activity
        // response — this is what makes the marking visible to teachers.
        //
        // The lab's `completed` data is the RECORD (board, question coords,
        // band, essay, report) — it is not an ActivityResponsePayload and never
        // was. Forwarding it as one sent `upsert_activity_response` a call with
        // activity_type, mode and response_data all undefined; JSON dropped the
        // keys and PostgREST answered 404 "could not find the function" — on
        // every successful marking, silently, since the slide shipped. The
        // marking row still kept the report (commitMarking), so nothing was
        // lost, but the activity/completion path never fired. Wrap it here.
        onActivityResponse?.(toActivityResponse(payload.data));
      }
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [sendInit, onActivityResponse]);

  if (blocked) {
    return (
      <div className="h-full flex items-center justify-center p-8 text-center text-sm opacity-80">{blocked}</div>
    );
  }

  return (
    <iframe
      ref={iframeRef}
      src={`${LAB_ORIGIN}/?embed=1`}
      title={data.title || 'The Essay Lab'}
      className="w-full h-full border-0"
      allow="camera; clipboard-write; fullscreen"
    />
  );
}

function EssayMarkingEditor({
  data,
  theme,
  onUpdate,
}: {
  data: EssayMarkingSlideData;
  theme: Theme;
  onUpdate: (data: EssayMarkingSlideData) => void;
}) {
  // Fetched, not imported. The engine package carries `memoContent` — the
  // official memo's expected answers — so importing it into a browser component
  // would ship the mark scheme for every question to anyone who fetched the
  // chunk. The endpoint returns only the public past-paper statement, and it
  // reads the same packs the marking path gates on, so the picker cannot offer
  // a question that marking would then refuse.
  const [options, setOptions] = useState<{ topicId: string; year: number; sitting: string; paper: string; label: string; prompt: string }[]>([]);
  // `loaded` distinguishes "this board has no questions yet" from "the list
  // hasn't come back". They look identical — an empty picker — and only one of
  // them is worth telling the author about.
  const [loaded, setLoaded] = useState(false);
  const board = asBoard(data.board);

  useEffect(() => {
    let cancelled = false;
    setLoaded(false);
    setOptions([]);
    (async () => {
      try {
        const { data: session } = await supabase.auth.getSession();
        const token = session.session?.access_token;
        if (!token) return;
        // Per board: the two sets are different questions, and the endpoint is
        // the one that knows which packs exist. Asking without a board would
        // list the NSC's under an IEB slide.
        const res = await fetch(`/api/ai/mark-essay?board=${encodeURIComponent(board)}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) return;
        const payload = await res.json();
        if (cancelled) return;
        setOptions(
          (payload.questions ?? []).map((q: { topicId: string; year: number; sitting: string; paper: string; label: string; qNo: string; prompt: string }) => ({
            topicId: q.topicId,
            year: q.year,
            sitting: q.sitting,
            paper: q.paper,
            label: `${q.label} — ${q.year} ${q.paper} ${q.qNo}`,
            prompt: q.prompt,
          })),
        );
        setLoaded(true);
      } catch {
        // Leaves the picker empty; the author sees no options rather than a
        // broken editor.
      }
    })();
    return () => { cancelled = true; };
  }, [board]);

  const selectedKey = `${data.topicId}|${data.year}|${data.paper}`;

  /**
   * Switching board CLEARS the question, and has to.
   *
   * {topicId, year, sitting, paper} is only unique within a board — the same
   * four values name a different question, with a different memo, on the other
   * one. Carrying them across would leave a slide whose label says one thing
   * and whose marking request asks for another; at best the endpoint's pack
   * gate refuses it (an author confused by a question that "exists"), at worst
   * the coordinates happen to resolve and a learner is marked against the wrong
   * board's memo.
   */
  const changeBoard = (next: Board) => {
    if (next === board) return;
    onUpdate({ ...data, board: next, topicId: '', year: 0, sitting: 'Nov', paper: '', questionLabel: '' });
  };

  return (
    <div className="max-w-2xl mx-auto px-4 py-6 space-y-4" style={slideTypographyStyle(theme, 'body')}>
      <label className="block">
        <span className="text-sm font-medium" style={{ color: theme.colors.text }}>Title</span>
        <input
          value={data.title}
          onChange={(e) => onUpdate({ ...data, title: e.target.value })}
          className="mt-1 w-full rounded border px-3 py-2"
        />
      </label>

      <label className="block">
        <span className="text-sm font-medium" style={{ color: theme.colors.text }}>Examining board</span>
        <select
          value={board}
          onChange={(e) => changeBoard(e.target.value as Board)}
          className="mt-1 w-full rounded border px-3 py-2"
        >
          {BOARDS.map((b) => (
            <option key={b} value={b}>{BOARD_LABELS[b]}</option>
          ))}
        </select>
        <span className="mt-1 block text-xs opacity-60" style={{ color: theme.colors.text }}>
          Picks the question set, the memo and the marking grid. Changing it clears the question below.
        </span>
      </label>

      <label className="block">
        <span className="text-sm font-medium" style={{ color: theme.colors.text }}>Question</span>
        <select
          value={selectedKey}
          onChange={(e) => {
            const opt = options.find((o) => `${o.topicId}|${o.year}|${o.paper}` === e.target.value);
            if (opt) {
              onUpdate({
                ...data,
                // Stamped explicitly, so a slide authored before boards existed
                // records the board it was actually picked under rather than
                // leaving it to be inferred later.
                board,
                topicId: opt.topicId,
                year: opt.year,
                sitting: opt.sitting,
                paper: opt.paper,
                questionLabel: opt.prompt,
              });
            }
          }}
          className="mt-1 w-full rounded border px-3 py-2"
        >
          <option value="|0|">Choose a question…</option>
          {options.map((o) => (
            <option key={`${o.topicId}|${o.year}|${o.paper}`} value={`${o.topicId}|${o.year}|${o.paper}`}>
              {o.label}
            </option>
          ))}
        </select>
      </label>

      {loaded && options.length === 0 && (
        <p className="text-sm rounded border px-3 py-2" style={{ color: theme.colors.text, opacity: 0.85 }}>
          No {BOARD_LABELS[board]} questions are available yet — the questions, memos and marking grid
          for this board are still being built. The slide will work the moment they land; nothing else
          about it has to change.
        </p>
      )}

      {data.questionLabel && (
        <blockquote className="border-l-4 pl-3 py-1 text-sm italic opacity-80" style={{ borderColor: theme.colors.accent || theme.colors.primary, color: theme.colors.text }}>
          {data.questionLabel}
        </blockquote>
      )}

      {/* No "not reworked yet" warning here any more, and none is needed: the
          engine only offers the Essay Lab set, so every question in the picker
          is a rework and no verbatim exam or memo wording can reach a learner.
          The warning existed because the picker also listed the calibration-year
          packs — it apologised for a choice the author should never have had. */}

      <p className="text-xs opacity-60" style={{ color: theme.colors.text }}>
        Students see the Essay Lab here ({LAB_ORIGIN.replace(/^https?:\/\//, '')}), running as{' '}
        {BOARD_LABELS[board]}. Each submission spends one of the learner's markings and takes about a
        minute to mark.
      </p>
    </div>
  );
}
